import { useEffect, useMemo, useState } from "react";
import { searchForInvestors } from "../services/investorService";
import { SearchableInvestorEntry } from "../types/SearchableInvestorEntry";
import { useInvestorDatabaseMetadata } from "./use-investor-database-metadata";

export interface SearchParams {
  stageFocus?: string;
  sectorFocus?: string;
}

export const useSearchableInvestorSearchResults = (
  externalSearchParams: SearchParams | null,
  { sort = "alphabetical" }: { sort?: "alphabetical" | "relevance" } = {},
) => {
  const { vcDirectoryMetadata } = useInvestorDatabaseMetadata();

  const [searchParams, setSearchParams] = useState<SearchParams | null>(null);
  const [results, setResults] = useState<SearchableInvestorEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (
        !vcDirectoryMetadata ||
        loading ||
        !externalSearchParams ||
        (searchParams?.sectorFocus === externalSearchParams.sectorFocus &&
          searchParams?.stageFocus === externalSearchParams.stageFocus)
      ) {
        return;
      }

      setSearchParams(externalSearchParams);
      setLoading(true);
      setError(null);

      try {
        const searchResults = await searchForInvestors(
          vcDirectoryMetadata,
          {
            stageFocus: externalSearchParams?.stageFocus,
            sectorFocus: externalSearchParams?.sectorFocus,
          },
          { sort },
        );
        setResults(searchResults);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch search results",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [externalSearchParams, loading, searchParams, sort, vcDirectoryMetadata]);

  return useMemo(
    () => ({
      results,
      loading,
      error,
      hasResults: results.length > 0,
      resultCount: results.length,
    }),
    [results, loading, error],
  );
};
